/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BlockIcon from '@mui/icons-material/Block';
import Terminal, { NetworkAvailability, SpaceNetworkInfo } from '../../shared/entities/terminal';
import { TerminalIconStaticMarker } from './TerminalIconStaticMarker';
import { TerminalIconStaticMarkerStylesForTerminalList } from './TerminalIconStaticMarkerStyles';
import CollapseListItem from './PlatformListCollapseListItem';
import TransportMonitoringCollapseListItem from '../isanPortal/transportMonitoring/TransportMonitoringPlatformListCollapseListItem';
import { TerminalListItem } from '../../store';
import { updateList } from './PlatformList';
import { getSelectedTerminalTypeLabelBasedOnTab } from '../../utils/TerminalUtilities';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import appTheme from '../common/theme/appTheme.json';
import { isNil } from 'lodash';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import { shallowEqual, useSelector } from 'react-redux';
import { selectAppConfig } from '../../store';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    visibilityToggleContainer: {
      right: 0,
      paddingRight: '8px',
    },
    visibilityIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    collapseIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    blockIconContainer: {
      padding: '17px 12px 12px 12px',
    },
    blockIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    leftPrimaryText: {
      left: '98px',
      top: '10px',
      color: '#ffffff',
      fontSize: '16px',
      letterSpacing: '0.4px',
      lineHeight: '20px',
      position: 'absolute',
    },
    leftSecondaryText: {
      left: '98px',
      top: '30px',
      color: '#ffffff',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '16px',
      position: 'absolute',
    },
  }),
);
export default function PlatformListItemComponent(props: {
  terminalListItem: TerminalListItem;
  terminalsInGroup: Terminal[];
  collapsedDatapathUuids: { [datapathUuid: string]: boolean };
  selectedDatapathUuid: string | undefined;
  selectedTerminalTabIndex: number | undefined;
  listRef: any;
  hideTtho?: boolean;
  // Item Actions
  onRowClickedHandler: (datapathUuid: string, index: number) => void;
  onRowCollapseClickedHandler: (datapathUuid: string) => void;
  onRowHideClickedHandler: (datapathUuid: string) => void;

  // Subitem actions
  onRowSubItemClickedHandler: (datapathUuid: string, tabIndex: number | undefined) => void;
}) {
  const {
    terminalListItem,
    terminalsInGroup,
    listRef,
    selectedTerminalTabIndex,
    hideTtho,
    onRowClickedHandler,
    onRowCollapseClickedHandler,
    onRowHideClickedHandler,
    onRowSubItemClickedHandler,
  } = props;
  const appConfig = useSelector(selectAppConfig, shallowEqual);

  const classes = useStyles();

  const collapseButton = useMemo(
    () => (
      <IconButton
        data-test={`platform-list-item__toggle-row-collapse-state__${terminalListItem.datapathUuidsInGroup.join('_')}`}
        style={{ zIndex: 50, marginLeft: '10px' }}
        onClick={(event: React.MouseEvent) => {
          updateList(listRef);
          event.stopPropagation();
          onRowCollapseClickedHandler(terminalListItem.datapathUuidsInGroup[0]);
        }}
      >
        {terminalListItem.collapsed ? (
          <KeyboardArrowRight
            data-test={`platform-list-item__toggle-row-collapse-state--collapsed-icon__${terminalListItem.datapathUuidsInGroup.join(
              '_',
            )}`}
            className={classes.collapseIcon}
          />
        ) : (
          <KeyboardArrowDown
            data-test={`platform-list-item__toggle-row-collapse-state--expanded-icon__${terminalListItem.datapathUuidsInGroup.join(
              '_',
            )}`}
            className={classes.collapseIcon}
          />
        )}
      </IconButton>
    ),
    [
      classes.collapseIcon,
      onRowCollapseClickedHandler,
      terminalListItem.collapsed,
      terminalListItem.datapathUuidsInGroup,
      listRef,
    ],
  );
  const terminalMarker = useMemo(
    () => (
      <ListItemIcon
        data-test="terminal-marker"
        style={{
          opacity: terminalListItem.hidden ? 0.5 : 1,
          top: '4px',
          position: 'absolute',
        }}
      >
        <TerminalIconStaticMarker
          datapathUuid={terminalListItem.datapathUuidsInGroup[0]}
          platformType={terminalListItem.platformType}
          showRing2={terminalListItem.showRing2}
          showRing3={terminalListItem.showRing3}
          connectivityStatus={terminalListItem.connectivityStatus}
          markerStyles={TerminalIconStaticMarkerStylesForTerminalList}
          appConfig={appConfig}
        />
      </ListItemIcon>
    ),
    [terminalListItem, appConfig],
  );

  const terminalText = useMemo(() => {
    const getNetworkStatusLine = () => {
      const spaceNetworks = terminalListItem.spaceNetworks ?? [];
      return (
        <span>
          {spaceNetworks.map((spaceNetworkInfo: SpaceNetworkInfo, index: number) => {
            return (
              <span
                key={spaceNetworkInfo.networkName}
                data-test={`platform-list-item__terminals-on-spacecraft-list__display-all-network__${terminalListItem.terminalDisplayName}`}
              >
                <span
                  style={{
                    color:
                      spaceNetworkInfo.networkAvailability === NetworkAvailability.AVAILABLE
                        ? appTheme.svNetworkIsAvailableTextColor
                        : spaceNetworkInfo.networkAvailability === NetworkAvailability.UNAVAILABLE
                        ? appTheme.svNetworkIsUnavailableTextColor
                        : appTheme.svNetworkIsOfflineTextColor,
                  }}
                >
                  {spaceNetworkInfo.networkName}
                </span>
                {index < spaceNetworks.length - 1 ? <span>&nbsp;|&nbsp;</span> : <span></span>}
              </span>
            );
          })}
        </span>
      );
    };

    return (
      <Box>
        {terminalMarker}
        {terminalListItem.platformType === TerminalPlatformsEnum.SpaceVehicle ? (
          <ListItemText
            key={`${terminalListItem.datapathUuid}-main-tab`}
            data-test="terminal-list-item-text"
            classes={{ primary: classes.leftPrimaryText, secondary: classes.leftSecondaryText }}
            primary={
              <div>
                <span>{terminalListItem.terminalDisplayName}</span>
                &nbsp;
                {terminalListItem.spaceNetworks?.length ? (
                  <span
                    style={{ fontWeight: 300, fontSize: '14px' }}
                    data-test={`terminal-list-item-text__space-vehicle-network-count`}
                  >
                    ({terminalListItem.spaceNetworks.length})
                  </span>
                ) : null}
              </div>
            }
            secondary={getNetworkStatusLine()}
          />
        ) : (
          <Box
            data-test="terminal-list-item-text"
            style={{
              opacity: terminalListItem.hidden ? 0.5 : 1,
              left: '98px',
              top: '12px',
              lineHeight: '32px',
              position: 'absolute',
            }}
          >
            {terminalListItem.terminalDisplayName}
          </Box>
        )}
      </Box>
    );
  }, [terminalListItem, terminalMarker, classes]);

  const item = useMemo(
    () => (
      <ListItem
        button
        data-test={`platform-list-item__${terminalListItem.datapathUuidsInGroup.join('_')}`}
        disableGutters
        selected={terminalListItem.selected}
        sx={{
          backgroundColor: terminalListItem.selected ? '#000000 !important' : appTheme.primaryBackgroundColor,
          maxHeight: '56px',
          minHeight: '56px',
          letterSpacing: '0.4px',
          '&:hover': {
            backgroundColor: '#0D1418',
          },
        }}
        onClick={(event: React.MouseEvent) => {
          updateList(listRef);
          onRowClickedHandler(terminalListItem.datapathUuidsInGroup[0], 0);
        }}
      >
        {collapseButton}
        {terminalText}
        <ListItemSecondaryAction className={classes.visibilityToggleContainer}>
          {terminalListItem.restrictionEnabled ? (
            <div className={classes.blockIconContainer}>
              <BlockIcon
                data-test={`platform-list-item__block-icon__${terminalListItem.datapathUuidsInGroup.join('_')}`}
                className={classes.blockIcon}
              />
            </div>
          ) : (
            <IconButton
              data-test={`platform-list-item__row-hide-toggle__${terminalListItem.datapathUuidsInGroup.join('_')}`}
              onClick={(event: React.MouseEvent) => {
                updateList(listRef);
                onRowHideClickedHandler(terminalListItem.datapathUuidsInGroup[0]);
              }}
            >
              {terminalListItem.hidden ? (
                <VisibilityIcon
                  data-test={`platform-list-item__row-hide-toggle--unhide-icon__${terminalListItem.datapathUuidsInGroup.join(
                    '_',
                  )}`}
                  className={classes.visibilityIcon}
                />
              ) : (
                <VisibilityOffIcon
                  data-test={`platform-list-item__row-hide-toggle--hide-icon__${terminalListItem.datapathUuidsInGroup.join(
                    '_',
                  )}`}
                  className={classes.visibilityIcon}
                />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    ),
    [
      terminalListItem,
      onRowClickedHandler,
      onRowHideClickedHandler,
      classes.visibilityIcon,
      classes.visibilityToggleContainer,
      classes.blockIcon,
      classes.blockIconContainer,
      collapseButton,
      terminalText,
      listRef,
    ],
  );

  const collapse = useMemo(
    () => (
      <List
        data-test={`platform-list-item__terminals-on-aircraft-list__${terminalListItem.datapathUuidsInGroup.join('_')}`}
        component="div"
        disablePadding
        style={{
          opacity: terminalListItem.hidden ? 0.5 : 1,
        }}
      >
        {terminalListItem.platformType === TerminalPlatformsEnum.SpaceVehicle
          ? (terminalListItem.spaceNetworks ?? []).map(
              (spaceNetworkInfo: SpaceNetworkInfo, index: number, array: SpaceNetworkInfo[]) => (
                <TransportMonitoringCollapseListItem
                  key={`${terminalListItem.datapathUuidsInGroup[0]}-network-${index}`}
                  primaryDatapathUuidInGroup={terminalListItem.datapathUuidsInGroup[0]}
                  spaceNetworkInfo={spaceNetworkInfo}
                  index={index}
                  total={array.length}
                  terminalTabIndex={index}
                  onRowClickedHandler={onRowSubItemClickedHandler}
                />
              ),
            )
          : terminalsInGroup
              .filter((terminal: Terminal) => !isNil(terminal))
              .map((terminal: Terminal, index: number, array: Terminal[]) => (
                <CollapseListItem
                  key={terminal.datapathUuid}
                  primaryDatapathUuidInGroup={array[0].datapathUuid}
                  terminal={terminal}
                  index={index}
                  total={(array || []).length}
                  selected={
                    !!terminalListItem.selected &&
                    (getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminal) ||
                      array[0].terminalTypeLabel) === terminal.terminalTypeLabel
                  }
                  hideTtho={!!hideTtho || terminalListItem.platformType !== TerminalPlatformsEnum.Airborne}
                  onRowClickedHandler={onRowSubItemClickedHandler}
                />
              ))}
      </List>
    ),
    [terminalsInGroup, terminalListItem, selectedTerminalTabIndex, hideTtho, onRowSubItemClickedHandler],
  );
  return (
    <div data-test={`platform-container`} key={terminalListItem.datapathUuidsInGroup.join(',')}>
      <div data-test={`platform_${terminalListItem.datapathUuidsInGroup.join('_')}`}>
        {item}
        <Divider />
        {!terminalListItem.collapsed && collapse}
        {!terminalListItem.collapsed && <Divider />}
      </div>
    </div>
  );
}
